/**
 * Uprawnienia po stronie interfejsu.
 *
 * UWAGA: to jest wyłącznie warstwa wygody — ukrywa przyciski, których i tak
 * nie da się użyć. Jedynym źródłem prawdy jest backend, który weryfikuje
 * każde żądanie niezależnie. Zmiana czegokolwiek tutaj nie daje dostępu
 * do danych.
 *
 * Odzwierciedla macierz z docs/ROLE-I-UPRAWNIENIA.md.
 */

import type { User, UserRole } from '@/api/types'

export const ROLE_LABELS: Record<UserRole, string> = {
  OWNER: 'Właściciel',
  ADMIN: 'Administrator',
  TEACHER: 'Nauczyciel',
}

export const ROLE_DESCRIPTIONS: Record<UserRole, string> = {
  OWNER: 'Dostęp do wszystkich oddziałów i pełne zarządzanie systemem',
  ADMIN: 'Zarządzanie przypisanymi oddziałami i kontami nauczycieli',
  TEACHER: 'Własny plan zajęć, uczniowie, obecności i rezerwacja sal',
}

export const isOwner = (user: User | null): boolean => user?.role === 'OWNER'

export const isAdmin = (user: User | null): boolean =>
  user?.role === 'OWNER' || user?.role === 'ADMIN'

export const isTeacher = (user: User | null): boolean => user?.role === 'TEACHER'

/**
 * Czy konto przegląda zgłoszenia błędów.
 *
 * Wyłącznie konto serwisowe wykonawcy — także nie właścicielka. Zgłoszenia to
 * kolejka pracy nad aplikacją, a nie sprawa szkoły, więc trafiają wprost do
 * tego, kto je naprawia.
 */
export const canReviewFeedback = (user: User | null): boolean => user?.is_service === true

/** Czy użytkownik widzi moduł zarządzania kontami. */
export const canManageUsers = isAdmin

/** Czy użytkownik widzi listę kadry.
 *
 *  Nauczyciel nie — swój profil otwiera z Ustawień, a dane zawodowe kolegów
 *  go nie dotyczą. Backend odrzuca takie żądanie niezależnie od interfejsu. */
export const canBrowseTeachers = isAdmin

/**
 * Czy użytkownik rezerwuje salę poza zajęciami (remont, spotkanie, nagranie).
 *
 * Wszyscy poza nauczycielem — tak samo jak na serwerze. Nauczyciel rezerwacje
 * **widzi** w grafiku, bo inaczej wpisywałby lekcje w termin, którego system i
 * tak nie przyjmie; o zamknięciu sali decyduje jednak administracja.
 */
export const canManageRoomBookings = (user: User | null): boolean =>
  user !== null && !isTeacher(user)

/** Czy użytkownik może zakładać i edytować oddziały (tylko właściciel). */
export const canManageLocations = isOwner

/** Role, które dany użytkownik może nadać nowemu kontu. */
export function assignableRoles(user: User | null): UserRole[] {
  if (isOwner(user)) return ['OWNER', 'ADMIN', 'TEACHER']
  if (user?.role === 'ADMIN') return ['TEACHER']
  return []
}

/** Czy użytkownik może edytować wskazane konto. */
export function canEditUser(actor: User | null, target: User): boolean {
  if (isOwner(actor)) return true
  // Administrator oddziału zarządza wyłącznie nauczycielami.
  return actor?.role === 'ADMIN' && target.role === 'TEACHER'
}

/** Czy użytkownik może dezaktywować wskazane konto (nigdy własnego). */
export function canDeactivateUser(actor: User | null, target: User): boolean {
  return actor?.id !== target.id && canEditUser(actor, target)
}
